import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const EMPTY = { name: "", email: "", password: "", role: "supervisor" };
const err = (e) => toast.error(e.response?.data?.detail?.toString?.() || "Something went wrong");

export const UsersManager = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(err), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const add = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post("/users", f); setF(EMPTY); toast.success("Account created"); load(); } catch (e2) { err(e2); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete account ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Account deleted"); load(); } catch (e) { err(e); }
  };
  const reset = async (u) => {
    const p = window.prompt(`New password for ${u.email} (min 6 characters):`);
    if (!p) return;
    try { await api.put(`/users/${u.id}/password`, { password: p }); toast.success("Password updated"); } catch (e) { err(e); }
  };

  return (
    <div data-testid="users-manager" className="panel p-5 lg:col-span-2">
      <h3 className="mb-1 font-display text-xl font-bold">Supervisor Accounts</h3>
      <p className="mb-4 text-sm text-[color:var(--muted)]">Only logged-in supervisors can edit. Admins can manage accounts.</p>
      <form onSubmit={add} className="mb-4 grid gap-2 sm:grid-cols-[1fr_1.3fr_1fr_140px_auto]">
        <Input data-testid="user-name-input" placeholder="Name" value={f.name} onChange={set("name")} />
        <Input data-testid="user-email-input" type="email" required placeholder="Email" value={f.email} onChange={set("email")} />
        <Input data-testid="user-password-input" type="password" required placeholder="Password (min 6)" value={f.password} onChange={set("password")} />
        <Select value={f.role} onValueChange={(v) => setF((p) => ({ ...p, role: v }))}>
          <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="supervisor">Supervisor</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
        </Select>
        <Button data-testid="user-add-button" type="submit" className="bg-amber-500 text-black hover:bg-amber-400"><UserPlus className="mr-1 h-4 w-4" /> Add</Button>
      </form>
      <div className="overflow-x-auto">
        <table data-testid="users-table" className="w-full min-w-[560px] text-sm">
          <thead><tr className="border-b border-[color:var(--border)] text-left">{["Name", "Email", "Role", ""].map((h) => <th key={h} className="label px-3 py-2 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[color:var(--border)] last:border-0">
                <td className="px-3 py-2.5 font-display font-semibold">{u.name}{u.id === me?.id && <span className="ml-2 font-mono text-[10px] text-amber-400">(you)</span>}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2.5"><span className={`rounded-sm border px-2 py-0.5 font-mono text-[11px] uppercase ${u.role === "admin" ? "border-amber-500/40 text-amber-400" : "border-sky-500/40 text-sky-400"}`}>{u.role}</span></td>
                <td className="px-3 py-2.5 text-right">
                  <Button size="icon" variant="ghost" title="Reset password" data-testid={`user-reset-${u.id}`} onClick={() => reset(u)}><KeyRound className="h-4 w-4" /></Button>
                  {u.id !== me?.id && <Button size="icon" variant="ghost" title="Delete" data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
